import React, { useState } from "react";

import { updateOwnersAsEmployees } from "EmployerApplication/Actions";
import { api } from "EmployerApplication/urls";
import createDecorator from "final-form-focus";
import isIntuit from "Intuit/isIntuit";
import { Form } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { useDispatch } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import companyToken from "Utils/companyToken";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";

import Button from "Cobalt/Button";
import { Callout } from "Cobalt/Callout";
import AppearingElement from "Common/AppearingElement";
import { SIFormRadioGroup, SIFinalFormField, SIFormSelectWithSearch } from "Common/forms";
import { Col, Row } from "Common/Grid";
import { Div } from "Common/HTML";
import PartnerContext from "Common/PartnerContext";
import RemoveIcon from "Common/RemoveIcon";
import { Caption, Body2 } from "Theme/BodyText";

import makeBem from "Utils/makeBem";

const bem = makeBem("OwnersAsEmployees");
const focusOnErrors = createDecorator();

export const OwnersAsEmployeesForm = ({ employees, ownersAsEmployees }) => {
  const hasEmployeesWithOverride = employees && employees.some((employee) => employee.override_employee_type);
  const initialValues = {
    ownerAsEmployee: hasEmployeesWithOverride ? "Yes" : ownersAsEmployees?.ownerAsEmployee,
    ownerAsEmployeeWages: hasEmployeesWithOverride ? "No" : ownersAsEmployees?.ownerAsEmployeeWages,
  };

  const owners = employees.filter((employee) => employee.employee_type === "owner" && employee.override_employee_type);
  const [ownerEmployees, setOwnerEmployees] = useState(owners.map((owner) => owner.hash_id));
  const getNameByHashId = (hashId) => {
    const employee = employees.find((e) => e.hash_id === hashId);
    return employee?.full_name;
  };

  const employeesWithoutOwners = employees.filter((employee) => !ownerEmployees.includes(employee.hash_id));

  const addEmployeeOwner = async (hashId) => {
    await fetchWithCSRFToken(api.enableOverrideEmployeeType, {
      method: "PUT",
      body: JSON.stringify({
        employee_hash_id: hashId,
        company_id_token: companyToken(),
      }),
      processData: false,
    });
    setOwnerEmployees([...ownerEmployees, hashId]);
  };

  const removeEmployeeOwner = async (hashId) => {
    await fetchWithCSRFToken(api.disableOverrideEmployeeType, {
      method: "PUT",
      body: JSON.stringify({
        employee_hash_id: hashId,
        company_id_token: companyToken(),
      }),
      processData: false,
    });
    setOwnerEmployees(ownerEmployees.filter((employeeHash) => employeeHash !== hashId));
  };

  const dispatch = useDispatch();

  const handleFormSubmit = (formValues) => {
    dispatch(updateOwnersAsEmployees(formValues));
  };

  return (
    <Div className={bem()}>
      <Form employees={employees} decorators={[focusOnErrors]} onSubmit={handleFormSubmit} initialValues={initialValues}>
        {({ handleSubmit, values: formValues }) => (
          <form onSubmit={handleSubmit}>
            <PartnerContext.Consumer>
              {() => (
                <Div mt={4}>
                  <SIFinalFormField
                    name="ownerAsEmployee"
                    label="Is any person in the list above an owner of the company?"
                    component={SIFormRadioGroup}
                    inline
                    options={{
                      Yes: "Yes",
                      No: "No",
                    }}
                    px={0}
                    noBounce
                    errorRight
                  />

                  <OnChange name="ownerAsEmployee">
                    {(value, previous) => {
                      if (value !== previous) {
                        AnalyticsTracking.trackEvent("cta_clicked", {
                          label: "is_employee_owner",
                          value,
                        });
                      }
                    }}
                  </OnChange>
                  <AppearingElement show={formValues.ownerAsEmployee === "Yes"}>
                    <SIFinalFormField
                      name="ownerAsEmployeeWages"
                      label="Do all owners in the list above receive wages as employees on payroll?"
                      inline
                      component={SIFormRadioGroup}
                      options={{
                        Yes: "Yes",
                        No: "No",
                      }}
                      px={0}
                      noBounce
                      errorRight
                    />

                    <OnChange name="ownerAsEmployeeWages">
                      {(value, previous) => {
                        if (value !== previous) {
                          AnalyticsTracking.trackEvent("cta_clicked", {
                            label: "do_owners_receive_wages",
                            value,
                          });

                          if (value === "Yes") {
                            handleSubmit({
                              ...formValues,
                              ownerAsEmployeeWages: value,
                            });
                          }
                        }
                      }}
                    </OnChange>
                  </AppearingElement>
                </Div>
              )}
            </PartnerContext.Consumer>
            <PartnerContext.Consumer>
              {() => (
                <>
                  <AppearingElement show={formValues.ownerAsEmployeeWages === "No" && formValues.ownerAsEmployee === "Yes"}>
                    <SIFinalFormField
                      name="ownerAsEmployeeName"
                      label="Select the owners who do not receive wages as employees on payroll"
                      component={SIFormSelectWithSearch}
                      px={0}
                      md={8}
                      componentProps={{
                        key: `owners-${formValues.ownerAsEmployeeWages}`,
                        value: formValues.ownerAsEmployeesWages || "",
                        options: employeesWithoutOwners.map((employee) => [employee.hash_id, employee.full_name]),

                        closeMenuOnSelect: true,
                        blurInputOnSelect: true,
                        escapeClearsValue: true,
                        iconName: "search",
                        type: "submit",
                      }}
                    />

                    <OnChange name="ownerAsEmployeeName">
                      {(value, previous) => {
                        if (value && value !== previous) {
                          addEmployeeOwner(value).then(() => {
                            handleSubmit({
                              ...formValues,
                              ownerAsEmployeeName: value,
                            });

                            AnalyticsTracking.trackEvent("cta_clicked", {
                              label: "owner_identified",
                            });
                          });
                        }
                      }}
                    </OnChange>
                  </AppearingElement>
                  {formValues.ownerAsEmployeeWages === "No" && formValues.ownerAsEmployee === "Yes" && (
                    <AppearingElement show={ownerEmployees.length}>
                      <Row>
                        <Col md={8} pl={4} pr={0}>
                          <Caption color="success" className={bem("OwnerHeader")}>
                            OWNER
                          </Caption>

                          {ownerEmployees.map((ownerEmployee) => (
                            <Div className={bem("OwnerRow")} key={`owner-${ownerEmployee}`}>
                              <Body2 pt={isIntuit() ? 2 : 3}>{getNameByHashId(ownerEmployee)}</Body2>
                              <Button
                                color="destructive"
                                size="destructive"
                                onClick={() => {
                                  removeEmployeeOwner(ownerEmployee).then(() => {
                                    handleSubmit({
                                      ...formValues,
                                      ownerAsEmployeeName: null,
                                    });
                                  });
                                }}
                                aria-label="Remove employee from owner list"
                                type="submit"
                              >
                                <RemoveIcon />
                              </Button>
                            </Div>
                          ))}
                        </Col>
                      </Row>
                    </AppearingElement>
                  )}
                </>
              )}
            </PartnerContext.Consumer>

            <AppearingElement show={formValues.ownerAsEmployee === "Yes" && formValues.ownerAsEmployeeWages === "Yes"}>
              <Callout type="success" showIcon body="Thanks, we have what we need." />
            </AppearingElement>
          </form>
        )}
      </Form>
    </Div>
  );
};

export default OwnersAsEmployeesForm;
